import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import './SettingsPage.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

const AdSensePlaceholder = ({ position }) => (
  <div className="adsense-placeholder">
    <div className="ad-content">
      <span className="ad-label">Advertisement</span>
      <p className="ad-text">AdSense Ad #{position}</p>
    </div>
  </div>
);

const SettingsPage = ({ user, token, onUpdate, onUpgradeClick }) => {
  const [dateOfBirth, setDateOfBirth] = useState('');
  const [ethnicity, setEthnicity] = useState('');
  const [gender, setGender] = useState('');
  const [location, setLocation] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  const isFree = user.tier === 'free';

  useEffect(() => {
    if (user.profile) {
      setDateOfBirth(user.profile.dateOfBirth || '');
      setEthnicity(user.profile.ethnicity || '');
      setGender(user.profile.gender || '');
      setLocation(user.profile.location || '');
    }
  }, [user]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setMessage('');
    setLoading(true);

    try {
      const response = await fetch(`${API_URL}/user/settings`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          dateOfBirth,
          ethnicity,
          gender,
          location
        })
      });

      if (response.ok) {
        setMessage('Settings saved successfully! ✨');
        onUpdate();
        setTimeout(() => setMessage(''), 3000);
      } else {
        const data = await response.json();
        setError(data.error || 'Failed to save settings');
      }
    } catch (err) {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="settings-page">
      <div className="cosmic-bg"></div>
      
      <div className="page-header">
        <h1 className="page-title">⚙️ Settings</h1>
        <p className="page-subtitle">Personalize your experience for more accurate predictions</p>
      </div>

      <div className="settings-content">
        <div className="settings-main">
          <div className="card settings-card">
            <div className="card-header">
              <h2 className="card-title">
                <span className="title-icon">👤</span>
                Personal Information
              </h2>
              <p className="card-subtitle">All fields are optional but help improve prediction accuracy</p>
            </div>

            <form onSubmit={handleSubmit} className="settings-form">
              <div className="form-row">
                <div className="input-group">
                  <label className="input-label">
                    <span className="label-icon">🎂</span>
                    Date of Birth
                  </label>
                  <input
                    type="date"
                    className="settings-input"
                    value={dateOfBirth}
                    onChange={(e) => setDateOfBirth(e.target.value)}
                  />
                  <span className="input-hint">Used for astrological calculations</span>
                </div>

                <div className="input-group">
                  <label className="input-label">
                    <span className="label-icon">⚧️</span>
                    Gender
                  </label>
                  <select
                    className="settings-select"
                    value={gender}
                    onChange={(e) => setGender(e.target.value)}
                  >
                    <option value="">Prefer not to say</option>
                    <option value="female">Female</option>
                    <option value="male">Male</option>
                    <option value="non-binary">Non-binary</option>
                    <option value="other">Other</option>
                  </select>
                </div>
              </div>

              <div className="form-row">
                <div className="input-group">
                  <label className="input-label">
                    <span className="label-icon">🌍</span>
                    Ethnicity
                  </label>
                  <input
                    type="text"
                    className="settings-input"
                    value={ethnicity}
                    onChange={(e) => setEthnicity(e.target.value)}
                    placeholder="e.g., Asian, Hispanic, etc."
                  />
                </div>

                <div className="input-group">
                  <label className="input-label">
                    <span className="label-icon">📍</span>
                    Location
                  </label>
                  <input
                    type="text"
                    className="settings-input"
                    value={location}
                    onChange={(e) => setLocation(e.target.value)}
                    placeholder="City, Country"
                  />
                  <span className="input-hint">Used for timezone and regional insights</span>
                </div>
              </div>

              {message && (
                <div className="success-message">
                  <span className="success-icon">✓</span>
                  {message}
                </div>
              )}

              {error && (
                <div className="error-message">
                  <span className="error-icon">⚠️</span>
                  {error}
                </div>
              )}

              <button 
                type="submit" 
                className="save-btn"
                disabled={loading}
              >
                {loading ? (
                  <>
                    <span className="spinner-small"></span>
                    <span>Saving...</span>
                  </>
                ) : (
                  <>
                    <span className="btn-icon">💾</span>
                    <span>Save Changes</span>
                  </>
                )}
              </button>
            </form>
          </div>

          {/* Account Info */}
          <div className="card account-card">
            <h2 className="card-title">
              <span className="title-icon">📊</span>
              Account Details
            </h2>
            
            <div className="account-info">
              <div className="info-item">
                <span className="info-label">Email:</span>
                <span className="info-value">{user.email}</span>
              </div>
              
              <div className="info-item">
                <span className="info-label">Current Plan:</span>
                <span className={`tier-badge tier-${user.tier}`}>
                  {user.tier.toUpperCase()}
                </span>
              </div>
              
              <div className="info-item">
                <span className="info-label">Member Since:</span>
                <span className="info-value">
                  {new Date(user.createdAt).toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                  })}
                </span>
              </div>
            </div>

            {user.tier === 'free' && (
              <div className="upgrade-prompt">
                <p>✨ Unlock more features with a premium plan!</p>
                <button className="upgrade-btn">
                  Upgrade Now
                </button>
              </div>
            )}

            {/* Disclaimer Link */}
            <div className="disclaimer-link-container">
              <Link to="/disclaimer" className="disclaimer-link">
                <span className="disclaimer-icon">⚠️</span>
                <span>View AI Usage & Disclaimer</span>
              </Link>
            </div>
          </div>
        </div>

        {/* AdSense for Free Tier */}
        {isFree && (
          <div className="settings-sidebar">
            <AdSensePlaceholder position={1} />
          </div>
        )}
      </div>
    </div>
  );
};

export default SettingsPage;